"use client"

import { useEffect, useState } from "react"
import ErrorPage from "@/components/error-page"
import {
  reportClientError,
  handleVeralteterBuild,
  istFremderDomEingriff,
  versucheDomEingriffZuHeilen,
  FREMDEINGRIFF_PRAEFIX,
} from "@/lib/client-error-report"

interface ErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

/**
 * Was der Gast sieht, wenn eine Übersetzung oder Erweiterung uns das DOM unter den
 * Füßen weggezogen hat. Kein Serverfehler — und vor allem keiner, für den der Gast
 * etwas kann. Deshalb sagt die Seite, was wirklich los ist, und was hilft.
 */
const ANZEIGEFEHLER_CONFIG = {
  title: "Kurz aus der Spur geraten!",
  subtitle: "Anzeigefehler",
  description:
    "Die Übersetzungsfunktion deines Browsers oder eine Erweiterung hat die Seite verändert, während wir sie aufgebaut haben. An deiner Buchung liegt es nicht — einmal neu aufbauen genügt meistens. Falls es wieder passiert: Seitenübersetzung für diese Seite ausschalten.",
  image: "/images/errors/error-000-kartbahn.webp",
  accent: "#FF7A00",
  showRetry: true,
}

export default function Error({ error, reset }: ErrorProps) {
  // Meldung UND Stack melden. Vorher stand hier `error.stack || error.message` — bei
  // WebKit liefert der Stack keinen Fehlertext, wodurch jede Auswertung nach dem
  // Fehlernamen ins Leere lief.
  const text = [
    error.message,
    error.stack,
    error.digest ? `(digest ${error.digest})` : "",
  ]
    .filter(Boolean)
    .join("\n")

  const fremdeingriff = istFremderDomEingriff(text)
  const [heiltGerade, setHeiltGerade] = useState(false)

  useEffect(() => {
    // Fremdeingriffe als eigene Klasse melden (WARN statt ERROR): Sie sind kein Defekt
    // unserer Anwendung, sollen aber zählbar bleiben — sonst lässt sich nie belegen,
    // ob die Selbstheilung unten tatsächlich greift.
    if (fremdeingriff) {
      reportClientError(`${FREMDEINGRIFF_PRAEFIX} ${text}`, "WARN")
      if (versucheDomEingriffZuHeilen(reset)) setHeiltGerade(true)
      return
    }

    reportClientError(`error-boundary: ${text}`)
    // Fehlt nur ein Chunk, weil während der Sitzung neu ausgeliefert wurde, ist die
    // Seite nicht kaputt — sie ist veraltet. Einmal neu laden genügt; der Merker in
    // sessionStorage verhindert eine Schleife.
    handleVeralteterBuild(text)
  }, [error, fremdeingriff, text, reset])

  // Während der Heilungsversuch läuft, keine Fehlerseite aufblitzen lassen.
  if (heiltGerade) return null

  // Ein Absturz im Browser ist kein Serverfehler. Die 500er-Seite sagt „Serverfehler"
  // und schickt den Gast damit auf die falsche Fährte — und uns in die Backend-Logs,
  // in denen dazu nichts steht, weil dort auch nichts passiert ist.
  if (fremdeingriff) {
    return <ErrorPage statusCode={0} reset={reset} customConfig={ANZEIGEFEHLER_CONFIG} customCode="Anzeige" />
  }

  const statusCode = extractStatusCode(error) ?? 500
  return <ErrorPage statusCode={statusCode} reset={reset} />
}

function extractStatusCode(error: Error & { digest?: string }): number | null {
  // Next.js sometimes includes status code info in the digest
  if (error.digest) {
    const match = error.digest.match(/(\d{3})/)
    if (match) {
      const code = parseInt(match[1], 10)
      if (code >= 400 && code < 600) return code
    }
  }
  // Check error message for HTTP status codes
  const msgMatch = error.message?.match(/\b(4\d{2}|5\d{2})\b/)
  if (msgMatch) return parseInt(msgMatch[1], 10)
  return null
}
